export interface Hsv {
	h: number
	s: number
	v: number
}

const clamp = (value: number, min: number, max: number) =>
	Math.min(max, Math.max(min, value))

export function normalizeHex(input: string): string | null {
	const raw = input.trim().replace(/^#/, "")
	if (/^[0-9a-f]{3}$/i.test(raw)) {
		const [r, g, b] = raw
		return `#${r}${r}${g}${g}${b}${b}`.toUpperCase()
	}
	if (/^[0-9a-f]{6}$/i.test(raw)) {
		return `#${raw}`.toUpperCase()
	}
	return null
}

export function hexToHsv(hex: string): Hsv | null {
	const normalized = normalizeHex(hex)
	if (!normalized) return null

	const r = parseInt(normalized.slice(1, 3), 16) / 255
	const g = parseInt(normalized.slice(3, 5), 16) / 255
	const b = parseInt(normalized.slice(5, 7), 16) / 255

	const max = Math.max(r, g, b)
	const min = Math.min(r, g, b)
	const delta = max - min

	let h = 0
	if (delta !== 0) {
		if (max === r) h = ((g - b) / delta) % 6
		else if (max === g) h = (b - r) / delta + 2
		else h = (r - g) / delta + 4
		h *= 60
		if (h < 0) h += 360
	}

	return {
		h,
		s: max === 0 ? 0 : delta / max,
		v: max,
	}
}

export function hsvToHex({ h, s, v }: Hsv): string {
	const hue = ((h % 360) + 360) % 360
	const sat = clamp(s, 0, 1)
	const val = clamp(v, 0, 1)

	const c = val * sat
	const x = c * (1 - Math.abs(((hue / 60) % 2) - 1))
	const m = val - c

	const [r, g, b] =
		hue < 60
			? [c, x, 0]
			: hue < 120
				? [x, c, 0]
				: hue < 180
					? [0, c, x]
					: hue < 240
						? [0, x, c]
						: hue < 300
							? [x, 0, c]
							: [c, 0, x]

	const toChannel = (channel: number) =>
		Math.round((channel + m) * 255)
			.toString(16)
			.padStart(2, "0")
			.toUpperCase()

	return `#${toChannel(r)}${toChannel(g)}${toChannel(b)}`
}
